<template>
  <div>
    <block-item-card
      :title="$t('EVE.Section.InternalThresholds.NevLimits.Title')"
      :tooltip="$t('EVE.Section.InternalThresholds.NevLimits.Tooltip')"
      class="fill-height"
    >
      <div class="pa-4">
        <div
          v-if="data"
          ref="nevChart"
          style="height: 600px; width: 100%;"
        />
      </div>
    </block-item-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'

export default {
  name      : 'InternalThresholds',
  components: { BlockItemCard },
  mixins    : [chartsColorMixin],
  props     : {
    data: {
      type   : Object,
      default: () => null
    }
  },
  data () {
    return {
      nevChart: null,
      niiChart: null
    }
  },
  computed: {
    chartData () {
      if (!this.data || !this.data.result || !this.data.result.internalThresholds) {
        // Fallback to hardcoded data based on the image
        return {
          categories   : ['Down 400', 'Down 300', 'Down 200', 'Down 100', 'Up 100', 'Up 200', 'Up 300', 'Up 400'],
          nevValues    : [-16, -10, -8, -6, -7, -10, -12, -18],
          niiValues    : [-22.5, -15, -13, -9, -9, -13, -15, -22.5],
          nevThresholds: [-16, -22],
          niiThresholds: [-9, -13, -15, -22.5, -25]
        }
      }

      const internalData = this.data.result.internalThresholds

      // Extract categories and values from the API data
      const categories = (internalData || []).map(item => item.title || 'Unknown')
      const nevValues = (internalData || []).map(item => item.actualValue || 0)
      const niiValues = (internalData || []).map(item => item.thresholdValue || 0)

      // Threshold values from API data
      const thresholdValues = (internalData || []).map(item => item.thresholdValue || 0)
      const actualValues = (internalData || []).map(item => item.actualValue || 0)

      // Calculate ghost bars - extend from actual value to min/max threshold
      const minThreshold = Math.min(...actualValues)
      const maxThreshold = Math.max(...actualValues)

      const nevGhosts = nevValues.map((value) => {
        // Ghost bar extends from actual value to the appropriate min/max threshold
        if (value >= 0) {
          // For positive values, extend to max threshold
          return maxThreshold - value
        } else {
          // For negative values, extend to min threshold
          return Math.abs(minThreshold - value)
        }
      })

      return {
        categories,
        nevValues,
        niiValues,
        nevGhosts,
        thresholdValues,
        minThreshold,
        maxThreshold,
        nevThresholds: [-16, -22],
        niiThresholds: [-9, -13, -15, -22.5, -25]
      }
    },
    nevChartOptions () {
      return {
        title  : { show: false },
        tooltip: {
          show       : true,
          trigger    : 'axis',
          axisPointer: {
            type: 'shadow'
          },
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : function (params) {
            if (params && params.length > 0) {
              // Find the main data series (NEV Limits)
              const mainData = params.find(param => param.seriesName === 'NEV Limits')
              if (mainData) {
                return `${mainData.name}<br/>Value: ${mainData.value}%`
              }
              // Fallback to first parameter if main series not found
              const data = params[0]
              return `${data.name}<br/>Value: ${data.value}%`
            }
            return ''
          }
        },
        grid: {
          left           : '0',
          right          : '5%',
          bottom         : '5%',
          top            : '5%',
          containLabel   : true,
          backgroundColor: this.themeColorRGBA('synthesis-brand-white')
        },
        xAxis: {
          type    : 'category',
          data    : this.chartData.categories,
          axisLine: {
            lineStyle: { color: this.themeColorRGBA('synthesis-ui-grey-02'), type: 'dashed' }
          },
          axisTick : { show: false },
          axisLabel: { show: false }
        },
        yAxis: {
          type     : 'value',
          min      : this.chartData.minThreshold,
          max      : this.chartData.maxThreshold,
          axisLine : { show: false },
          axisTick : { show: false },
          axisLabel: {
            show  : false,
            color : this.themeColorRGBA('synthesis-ui-grey-02'),
            data  : this.chartData.thresholdValues,
            margin: 10
          },
          splitLine: {
            show     : false,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02'),
              type : 'dashed'
            }
          },
          z: 1
        },
        series: [
          // Background bars (ghost bars) - extend from 0 to threshold boundaries
          {
            name    : 'Background',
            type    : 'bar',
            barWidth: '40%',
            data    : this.chartData.nevValues.map((value) => {
              // Background bar extends from 0 to the appropriate threshold boundary
              if (value >= 0) {
                // For positive values, background goes from 0 to max threshold
                return this.chartData.maxThreshold
              } else {
                // For negative values, background goes from 0 to min threshold
                return this.chartData.minThreshold
              }
            }),
            itemStyle: {
              color       : this.themeColorRGBA('synthesis-ui-grey-02', 0.3),
              borderRadius: [4, 4, 4, 4]
            },
            silent        : true,
            z             : 99,
            barGap        : '-100%',
            barCategoryGap: '20%'
          },
          // Actual value bars - start from 0
          {
            name     : 'NEV Limits',
            type     : 'bar',
            barWidth : '40%',
            data     : this.chartData.nevValues,
            itemStyle: {
              color       : this.themeColorRGBA('synthesis-ui-blue-04'),
              borderRadius: [4, 4, 4, 4]
            },
            label: {
              show     : true,
              position : 'outside',
              formatter: function (params) {
                return params.value + '%'
              },
              color        : this.themeColorRGBA('synthesis-ui-grey-01'),
              fontWeight   : 'bold',
              align        : 'center',
              verticalAlign: 'middle',
              distance     : 20
            },
            markLine: {
              silent: true,
              symbol: 'none',
              z     : 0, // Lower z-index to appear behind bars
              data  : this.chartData.thresholdValues.map(threshold => ({
                yAxis    : threshold,
                lineStyle: {
                  color: this.themeColorRGBA('synthesis-ui-grey-02'),
                  type : 'dashed',
                  width: 1
                },
                label: {
                  show     : true,
                  position : 'insideStartTop',
                  formatter: threshold + '%',
                  color    : this.themeColorRGBA('synthesis-brand-red'),
                  fontSize : 10,
                  offset   : [-5, 0]
                }
              }))
            },
            z             : 100,
            barGap        : '-100%',
            barCategoryGap: '20%'
          },
          // Category name boxes
          {
            name    : 'Category Names',
            type    : 'bar',
            barWidth: '40%',
            data    : this.chartData.nevValues.map((value, index) => ({
              value    : value >= 0 ? 0.1 : -0.1, // Small value to create a box
              itemStyle: {
                color       : this.themeColorRGBA('synthesis-ui-grey-02', 0.8),
                borderRadius: [2, 2, 2, 2]
              }
            })),
            label: {
              show     : true,
              // position: function (params) {
              //   return params.value >= 0 ? 'top' : 'bottom'
              // },
              formatter: function (params) {
                return this.chartData.categories[params.dataIndex]
              }.bind(this),
              color          : 'white',
              fontFamily     : 'Inter',
              letterSpacing  : 0,
              width          : 60,
              fontWeight     : 600,
              fontSize       : 10,
              align          : 'center',
              verticalAlign  : 'middle',
              backgroundColor: this.themeColorRGBA('synthesis-ui-blue-06', 0.8),
              borderColor    : this.themeColorRGBA('synthesis-ui-grey-06'),
              borderWidth    : 0,
              borderRadius   : 2,
              padding        : [4, 8, 4, 8],
              position       : 'outside',
              distance       : -15
              // gap            : 10
            },
            z             : 3,
            barGap        : '-100%',
            barCategoryGap: '20%'
          }
        ]
      }
    }
  },
  watch: {
    data: {
      handler (newData, oldData) {
        this.$nextTick(() => {
          if (this.nevChart) {
            this.nevChart.setOption(this.nevChartOptions)
          }
        })
      },
      deep     : true,
      immediate: true
    },
    chartData: {
      handler () {
        this.$nextTick(() => {
          if (this.nevChart) {
            this.nevChart.setOption(this.nevChartOptions)
          }
        })
      },
      deep: true
    }
  },
  activated () {
    this.$nextTick(() => {
      if (this.$refs.nevChart && !this.nevChart) {
        this.initCharts()
      } else if (this.nevChart && this.data) {
        // Re-initialize charts with current data
        this.nevChart.setOption(this.nevChartOptions)
        if (this.niiChart) {
          this.niiChart.setOption(this.niiChartOptions)
        }
      }
    })
  },
  mounted () {
    this.$nextTick(() => {
      this.initCharts()
    })
  },
  beforeDestroy () {
    if (this.nevChart) {
      this.nevChart.dispose()
    }
    if (this.niiChart) {
      this.niiChart.dispose()
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    initCharts () {
      try {
        if (this.$refs.nevChart) {
          this.nevChart = echarts.init(this.$refs.nevChart)
          this.nevChart.setOption(this.nevChartOptions)
          this.drawThresholdLines()
        }

        window.addEventListener('resize', this.handleResize)
      } catch (error) {
        // Error initializing charts
      }
    },
    drawThresholdLines () {
      if (!this.nevChart || !this.chartData.thresholdValues) return

      // Clear existing threshold lines
      this.nevChart.setOption({
        graphic: []
      })

      // Draw individual threshold lines
      const thresholdLines = this.chartData.thresholdValues.map((threshold, index) => {
        // Get the exact pixel position of the bar
        const barPixelPos = this.nevChart.convertToPixel('grid', [index, 0])
        const thresholdPixelPos = this.nevChart.convertToPixel('grid', [index, threshold])

        if (!barPixelPos || !thresholdPixelPos) return null

        // Calculate bar width in pixels
        const barWidth = this.nevChart.getWidth() * 0.4 / this.chartData.categories.length

        return {
          type : 'line',
          left : barPixelPos[0] - barWidth / 2,
          top  : thresholdPixelPos[1] - 2,
          shape: {
            x1: 0,
            y1: 0,
            x2: barWidth,
            y2: 0
          },
          style: {
            stroke   : this.themeColorRGBA('synthesis-brand-red'),
            lineDash : [5, 5],
            lineWidth: 1.5
          },
          z: 999
        }
      }).filter(line => line !== null)

      // Add the threshold lines to the chart
      this.nevChart.setOption({
        graphic: thresholdLines
      })
    },
    handleResize () {
      if (this.nevChart) {
        this.nevChart.resize()
        this.drawThresholdLines()
      }
    },
    refreshCharts () {
      if (this.data && this.nevChart) {
        this.nevChart.setOption(this.nevChartOptions)
        this.drawThresholdLines()
      }
    }
  }
}
</script>

<style scoped>
</style>
